/**
 * AudioWidget — a content-addressed audio clip (contentType "audio") presented
 * as a SoundCloud-style waveform player: a play/pause toggle, a bar waveform
 * whose accent copy fills left→right as it plays, and click-to-seek. Same asset
 * pipeline as ImageWidget/VideoWidget (hash → OPFS, resolve OPFS→peer→cloud,
 * publish bytes by sha), plus a precomputed amplitude envelope (`peaks`) stored
 * in contentProps so the player — and the published static page — draw the same
 * waveform with no client-side decode.
 *
 * Web-only (Web Audio decode + DOM), so it's registered in catalog.ts only and
 * never reaches the native (RN) bundle.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { css, html } from "react-strict-dom";
import { Badge, Button, FileTrigger, Icon, Tooltip } from "@duro-app/ui";
import type { FileTriggerHandle } from "@duro-app/ui";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import { layers } from "@duro-app/tokens/tokens/layers.css";
import { sizes } from "@duro-app/tokens/tokens/sizes.css";
import { typeScale, typography } from "@duro-app/tokens/tokens/typography.css";
import {
  microSpacing,
  radii,
  spacing,
} from "@duro-app/tokens/tokens/spacing.css";
import { useGridStore, useUpdateGrid } from "@fredericrous/grid";

import type { WidgetProps } from "./types.js";
import { useSiteId } from "../SiteContext.js";
import { getAssetFile, putAsset } from "../assets/opfsAssetStore.js";
import { extractPeaks } from "../assets/audioPeaks.js";
import { FLAT_PLACEHOLDER } from "../waveformSvg.js";
import {
  getCollabBlobResolver,
  getCollabBlobAnnounce,
} from "../assets/collabBlobResolver.js";
import {
  markBackingUp,
  useBackupStatus,
} from "../assets/collabBackupStatus.js";
import { useRoomPeerCount } from "../assets/collabPeers.js";
import { openMediaLibraryForPick } from "../mediaLibrary.js";
import { useAddDrag } from "../AddDragContext.js";
import { useMediaBindRef } from "./mediaBindRegistry.js";

const ACCEPTED =
  "audio/mpeg,audio/ogg,audio/wav,audio/webm,audio/mp4,audio/aac,audio/flac";

const styles = css.create({
  kindIcon: {
    display: "inline-flex",
    color: colors.textMuted,
  },
  // 80% of the tile, clamped — see ImageWidget's KindIcon.
  kindIconFluid: {
    width: "80%",
    height: "80%",
    minWidth: sizes.iconLg,
    minHeight: sizes.iconLg,
    maxWidth: sizes.glyphClampMax,
    maxHeight: sizes.glyphClampMax,
    alignItems: "center",
    justifyContent: "center",
  },
  playerRow: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    gap: spacing.ms,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: spacing.ms,
    paddingRight: spacing.ms,
    boxSizing: "border-box",
  },
  // The waveform: the unplayed bars, with the played copy clipped over them.
  wave: {
    position: "relative",
    flex: 1,
    height: "70%",
    minWidth: 0,
    cursor: "default",
  },
  waveInteractive: {
    cursor: "pointer",
  },
  waveLayer: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  // One bar per peak: the slots share the width equally, each bar centred in
  // its slot with the gap split as side margins (percent of the wave's width).
  bar: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    backgroundColor: "currentColor",
  },
  barGap: (sidePct: number) => ({
    marginLeft: `${sidePct}%`,
    marginRight: `${sidePct}%`,
  }),
  barHeight: (heightPct: number) => ({
    height: `${heightPct}%`,
  }),
  waveBase: {
    color: colors.border,
  },
  wavePlayed: (pct: number) => ({
    color: colors.accent,
    clipPath: `inset(0 ${100 - pct}% 0 0)`,
  }),
  // The static preview's play glyph (no control behind it).
  playGlyph: {
    flexShrink: 0,
    width: sizes.iconButton,
    height: sizes.iconButton,
    borderRadius: radii.full,
    backgroundColor: colors.accent,
    color: colors.accentContrast,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  playSlot: {
    flexShrink: 0,
  },
  time: {
    flexShrink: 0,
    fontSize: typeScale.fontSize1,
    fontVariantNumeric: "tabular-nums",
    color: colors.textMuted,
    minWidth: sizes.timeReadoutW,
    textAlign: "right",
  },
  tile: {
    position: "relative",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.bgCard,
    borderRadius: radii.sm,
  },
  // A dragged media asset hovering an EMPTY tile: the drop-to-fill ring.
  bindTarget: {
    backgroundColor: colors.infoBg,
    borderWidth: borders.strong,
    borderStyle: "solid",
    borderColor: colors.accent,
  },
  replaceSlot: {
    position: "absolute",
    top: spacing.xs,
    right: spacing.xs,
    zIndex: layers.raised,
  },
  uploadPrompt: {
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "center",
    gap: microSpacing.px6,
    color: colors.textMuted,
    fontSize: typeScale.fontSize2,
    width: "100%",
    height: "100%",
    padding: spacing.sm,
    boxSizing: "border-box",
  },
  // Fluid icon area — see ImageWidget's empty placeholder.
  iconArea: {
    flex: 1,
    minHeight: 0,
    width: "100%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  uploading: {
    color: colors.textMuted,
    fontSize: typeScale.fontSize2,
  },
  error: {
    position: "absolute",
    bottom: spacing.xs,
    left: spacing.xs,
    color: colors.error,
    fontSize: typography.fontSizeXs,
  },
  backupSlot: {
    position: "absolute",
    top: spacing.xs,
    left: spacing.xs,
  },
  // Mounts the file picker on an empty tile, where the tile itself is the
  // trigger (its click calls the picker's open()).
  pickerHidden: {
    display: "none",
  },
});

/** The kind glyph in a box that scales with the tile — 80% of it, clamped
 *  iconLg–glyphClampMax. Duro's Icon takes a fixed size token, so the glyph
 *  itself is the largest one (xxl), centred in that box. */
function KindIcon() {
  return (
    <html.span aria-hidden style={[styles.kindIcon, styles.kindIconFluid]}>
      {/* holds-until: Duro Icon gets a fluid size (https://github.com/fredericrous/duro-design-system/issues/106); the glyph stops at 48px meanwhile */}
      <Icon name="music" size="xxl" />
    </html.span>
  );
}

/** Stops a press from reaching the tile (its click opens the picker) or the
 *  grid item (its pointer-down starts a move). Duro's Button hands its onClick
 *  no event, so the slot around it does the stopping. */
function stop(e: { stopPropagation: () => void }) {
  e.stopPropagation();
}

// The bar geometry of waveformSvg (the published page's drawing): 30% of each
// slot is gap, and a silent bar keeps 6% of the height so silence still shows.
const BAR_GAP = 0.3;
const BAR_MIN_H = 0.06;

/** Each peak (0..100) as a bar height in % of the wave's height. */
function barHeights(peaks: readonly number[]): number[] {
  return peaks.map((peak) => {
    const p = Math.max(0, Math.min(100, peak));
    return (BAR_MIN_H + (p / 100) * (1 - BAR_MIN_H)) * 100;
  });
}

function fmtTime(s: number): string {
  if (!Number.isFinite(s) || s <= 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec < 10 ? "0" : ""}${sec}`;
}

export function AudioWidget({ itemId, contentProps, gridId }: WidgetProps) {
  const url = contentProps?.["url"] as string | undefined;
  const sha256 = contentProps?.["sha256"] as string | undefined;
  const ext = contentProps?.["ext"] as string | undefined;
  const mime = contentProps?.["mime"] as string | undefined;
  const preview = contentProps?.["preview"] as boolean | undefined;
  const peaks = contentProps?.["peaks"] as number[] | undefined;
  const duration = (contentProps?.["duration"] as number | undefined) ?? 0;
  const hasPeaks = Array.isArray(peaks) && peaks.length > 0;

  const siteId = useSiteId();
  const { updateItem } = useUpdateGrid(gridId);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [src, setSrc] = useState<string | undefined>(
    typeof url === "string" && url ? url : undefined,
  );
  const empty = !sha256 && !url;
  const isBindTarget = useAddDrag().bindTargetId === itemId;
  const bindRef = useMediaBindRef<HTMLDivElement>(
    "audio",
    gridId,
    itemId,
    empty,
  );
  const [playing, setPlaying] = useState(false);
  const [pct, setPct] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [metaDuration, setMetaDuration] = useState(0);
  const pickerRef = useRef<FileTriggerHandle>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const waveRef = useRef<HTMLDivElement>(null);
  const backup = useBackupStatus(sha256 ?? "");
  const roomPeers = useRoomPeerCount();

  const displayPeaks = hasPeaks ? peaks! : FLAT_PLACEHOLDER;
  const heights = useMemo(() => barHeights(displayPeaks), [displayPeaks]);
  // Each side margin, in % of the wave's width: half the gap of one slot.
  const barSide = ((BAR_GAP / 2) * 100) / Math.max(1, heights.length);
  const effDuration = duration || metaDuration;

  // Resolve the bytes: OPFS first, else a room peer (P2P), else the cloud asset
  // route once published. Mirrors VideoWidget's resolver.
  useEffect(() => {
    if (typeof url === "string" && url) {
      setSrc(url);
      return;
    }
    if (!sha256) {
      setSrc(undefined);
      return;
    }
    let objectUrl: string | null = null;
    let cancelled = false;
    void (async () => {
      const show = (file: Blob): void => {
        const typed = mime ? file.slice(0, file.size, mime) : file;
        objectUrl = URL.createObjectURL(typed);
        setSrc(objectUrl);
      };
      const file = siteId
        ? await getAssetFile(siteId, sha256, ext ?? "bin")
        : null;
      if (cancelled) return;
      if (file) return show(file);

      const resolver = siteId ? getCollabBlobResolver(siteId) : undefined;
      if (resolver) {
        const bytes = await resolver(sha256).catch(() => null);
        if (cancelled) return;
        if (bytes) {
          const peerFile = await getAssetFile(siteId!, sha256, ext ?? "bin");
          if (cancelled) return;
          if (peerFile) return show(peerFile);
        }
      }
      setSrc(siteId ? `/api/v1/assets/${siteId}/${sha256}` : undefined);
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, sha256, siteId, mime, ext]);

  // Lazy peak backfill: an asset picked "from library" (or an older item) has a
  // sha but no envelope. Fetch the bytes and compute peaks once so the waveform
  // is real, not the flat placeholder.
  useEffect(() => {
    if (!sha256 || hasPeaks || !siteId) return;
    let cancelled = false;
    void (async () => {
      const file = await getAssetFile(siteId, sha256, ext ?? "bin");
      if (!file || cancelled) return;
      const res = await extractPeaks(file);
      if (cancelled) return;
      const cur = useGridStore.getState().grids[gridId]?.items[itemId];
      if (!cur) return;
      updateItem(itemId, {
        contentProps: {
          ...(cur.contentProps ?? {}),
          peaks: res.peaks,
          duration: res.duration,
        },
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [sha256, hasPeaks, siteId, ext, gridId, itemId, updateItem]);

  // Smooth fill while playing (timeupdate alone is too coarse ~4 Hz).
  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    const tick = () => {
      const a = audioRef.current;
      if (a) {
        const d = a.duration || effDuration;
        setElapsed(a.currentTime);
        setPct(d > 0 ? Math.min(100, (a.currentTime / d) * 100) : 0);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, effDuration]);

  async function handleFile(file: File) {
    setError(null);
    setUploading(true);
    try {
      const asset = await putAsset(siteId ?? "", file);
      const { peaks: pk, duration: dur } = await extractPeaks(file);
      const next: Record<string, unknown> = { ...(contentProps ?? {}) };
      delete next["url"];
      next["sha256"] = asset.sha256;
      next["mime"] = asset.mime;
      next["ext"] = asset.ext;
      next["peaks"] = pk;
      next["duration"] = dur;
      updateItem(itemId, { contentProps: next });
      const announce = siteId ? getCollabBlobAnnounce(siteId) : undefined;
      if (announce) {
        announce(asset.sha256);
        markBackingUp(asset.sha256);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  }

  function toggle() {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) void a.play().catch(() => {});
    else a.pause();
  }

  // html.div's click event types pageX, not clientX: pageX − scrollX is the
  // same viewport x that getBoundingClientRect measures in.
  function seek(e: { pageX: number; stopPropagation: () => void }) {
    e.stopPropagation();
    const a = audioRef.current;
    const wave = waveRef.current;
    if (!a || !wave) return;
    const rect = wave.getBoundingClientRect();
    const frac = Math.min(
      1,
      Math.max(0, (e.pageX - window.scrollX - rect.left) / rect.width),
    );
    const d = a.duration || effDuration;
    if (d > 0) {
      a.currentTime = frac * d;
      setElapsed(frac * d);
      setPct(frac * 100);
    }
  }

  const bars = heights.map((h, i) => (
    <html.div
      key={i}
      style={[styles.bar, styles.barGap(barSide), styles.barHeight(h)]}
    />
  ));

  // The waveform (base + accent-fill) — shared by the live player and the
  // static preview. `interactive` wires the seek/keyboard affordances.
  const wave = (interactive: boolean) => (
    <html.div
      ref={waveRef}
      onClick={interactive ? seek : undefined}
      style={[styles.wave, interactive && styles.waveInteractive]}
    >
      <html.div aria-hidden style={[styles.waveLayer, styles.waveBase]}>
        {bars}
      </html.div>
      <html.div aria-hidden style={[styles.waveLayer, styles.wavePlayed(pct)]}>
        {bars}
      </html.div>
    </html.div>
  );

  const playGlyph = (
    <html.span aria-hidden style={styles.playGlyph}>
      <Icon name={playing ? "pause" : "play"} size="sm" />
    </html.span>
  );

  // Static preview (far zoom / read-only): base waveform + a play glyph, no
  // <audio>, no listeners.
  if (preview) {
    return (
      <html.div style={styles.playerRow}>
        {playGlyph}
        {wave(false)}
      </html.div>
    );
  }

  // The file picker: the visible Replace button on a filled tile; on an empty
  // tile it stays mounted (hidden) so the tile's click can open() it.
  const picker = (
    <FileTrigger
      ref={pickerRef}
      accept={ACCEPTED}
      variant="secondary"
      size="small"
      onSelect={(files) => {
        const f = files[0];
        if (f) void handleFile(f);
      }}
    >
      Replace
    </FileTrigger>
  );

  const player = src ? (
    <html.div style={styles.playerRow} onPointerDown={stop}>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d)) setMetaDuration(d);
        }}
        onTimeUpdate={(e) => {
          const a = e.currentTarget;
          const d = a.duration || effDuration;
          setElapsed(a.currentTime);
          if (d > 0) setPct(Math.min(100, (a.currentTime / d) * 100));
        }}
      />
      <html.span style={styles.playSlot} onClick={stop} onPointerDown={stop}>
        <Button
          variant="primary"
          size="small"
          aria-label={playing ? "Pause" : "Play"}
          onClick={toggle}
        >
          <Icon name={playing ? "pause" : "play"} size="sm" />
        </Button>
      </html.span>
      {wave(true)}
      <html.span style={styles.time}>
        {fmtTime(elapsed)} / {fmtTime(effDuration)}
      </html.span>
    </html.div>
  ) : null;

  return (
    <html.div
      // Empty tiles are drop-to-fill targets for a dragged audio asset.
      ref={bindRef}
      style={[styles.tile, isBindTarget && styles.bindTarget]}
      onClick={(e) => {
        if (!src && !uploading) {
          e.stopPropagation();
          pickerRef.current?.open();
        }
      }}
    >
      {player}
      {src && (
        <html.span
          style={styles.replaceSlot}
          onClick={stop}
          onPointerDown={stop}
        >
          {picker}
        </html.span>
      )}
      {!src && !uploading && (
        <html.span style={styles.uploadPrompt}>
          <html.span style={styles.iconArea}>
            <KindIcon />
          </html.span>
          Click to upload audio
          <html.span onClick={stop} onPointerDown={stop}>
            <Button
              variant="link"
              size="small"
              onClick={() =>
                openMediaLibraryForPick({ gridId, itemId, kind: "audio" })
              }
            >
              or choose from library
            </Button>
          </html.span>
        </html.span>
      )}
      {uploading && <html.span style={styles.uploading}>Uploading…</html.span>}
      {error && <html.span style={styles.error}>{error}</html.span>}
      {backup === "backing-up" && !uploading && (
        <html.span style={styles.backupSlot}>
          <Tooltip.Root
            placement="bottom"
            content={
              roomPeers > 0
                ? "This audio only exists on this device — keep this tab open until a collaborator loads it."
                : "This audio only exists on this device. It backs up once another collaborator is online."
            }
          >
            <Tooltip.Trigger>
              <Badge variant="warning" size="sm">
                backing up…
              </Badge>
            </Tooltip.Trigger>
          </Tooltip.Root>
        </html.span>
      )}
      {!src && (
        <html.span style={styles.pickerHidden} onClick={stop}>
          {picker}
        </html.span>
      )}
    </html.div>
  );
}
